<template>
  <div class="invoiceReceptionItems">
    <GridPanelHeader
      title=""
      :grid-ref="$refs.receptionItemsGrid"
      :show-add="true"
      :show-filter="false"
      :show-export="false"
      :show-columns="false"
      @add="handleAddClick"
    />
    <DxDataGrid
      :data-source="localData.items"
      :show-borders="true"
      :show-column-lines="true"
      :column-auto-width="false"
      :hover-state-enabled="true"
      :repaint-changes-only="true"
      :show-row-lines="false"
      :row-alternation-enabled="true"
      :allowColumnResizing="true"
      ref="receptionItemsGrid"
      :element-attr="gridAttributes"
      @editing-start="onEditingStart"
      @row-removing="onRowRemoving"
      @row-updated="onRowUpdated"
      @cell-prepared="onCellPrepared"
      key-expr="uuid"
      :column-min-width="50"
    >
      <DxColumn dataField="product_name" :caption="$t('common.product_name')" :allowEditing="false">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
      </DxColumn>
      <DxColumn dataField="product_code" :caption="$t('stock_management.products.code')" :allowEditing="false"> </DxColumn>
      <DxColumn dataField="document_number" :caption="$t('stock_management.document.document_number')" :allowEditing="false"> </DxColumn>
      <DxColumn
        :lookup="{
          dataSource: smProductVatList,
          valueExpr: 'id',
          displayExpr: 'name',
        }"
        dataField="vat_code"
        :caption="$t('stock_management.products.vat')"
        :allowEditing="true"
      >
      </DxColumn>
      <DxColumn dataField="unit_price" :caption="$t('stock_management.products.unit_price_net')" data-type="number" :allowEditing="true">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
        <DxCustomRule :validationCallback="(e) => e.value >= 0" :message="$t('validation.min_value', { min: 0 })" />
      </DxColumn>
      <DxColumn dataField="discount" :caption="$t('stock_management.invoice_management.discount') + '(%)'" data-type="number" :allowEditing="true">
        <DxRequiredRule :message="$t('validation.field_is_required')" />
        <DxCustomRule
          :validationCallback="(e) => e.value === null || e.value === undefined || e.value === '' || (e.value >= 0 && e.value <= 100)"
          :message="$t('validation.value_between', { min: 0, max: 100 })"
        />
      </DxColumn>
      <DxColumn
        dataField="unit_price_discounted"
        :caption="$t('stock_management.invoice_management.unit_price_discounted')"
        data-type="number"
        :allowEditing="false"
      ></DxColumn>
      <DxColumn dataField="quantity" :caption="$t('common.quantity')" data-type="number" :allowEditing="false"> </DxColumn>
      <DxColumn dataField="measure_unit" :caption="$t('common.mu')" :allowEditing="false"> </DxColumn>
      <DxColumn
        dataField="price_netto"
        :caption="$t('stock_management.invoice_management.price_netto')"
        data-type="number"
        :allowEditing="true"
      ></DxColumn>
      <DxColumn
        dataField="price_brutto"
        :caption="$t('stock_management.invoice_management.price_brutto')"
        data-type="number"
        :allowEditing="false"
      ></DxColumn>
      <DxEditing mode="cell" :allowUpdating="true" :allowAdding="false" :allowDeleting="true" :use-icons="true" :confirm-delete="true">
        <DxTexts
          :confirm-delete-message="
            $t('common.confirm_delete', {
              title: this.$t('common.product'),
            })
          "
          :confirm-delete-title="$t('common.confirm_delete_title')"
          :save-row-changes="$t('common.save')"
          :cancel-row-changes="$t('common.close')"
          :addRow="$t('common.add_new')"
          :editRow="$t('common.edit')"
          :deleteRow="$t('common.delete')"
        />
      </DxEditing>
      <DxHeaderFilter :visible="false" />
      <DxFilterRow :visible="false" :showOperationChooser="true" apply-filter="auto" />
      <DxSelection mode="single" />
    </DxDataGrid>
    <ReceptionSelector
      ref="receptionSelector"
      v-if="showReceptionDialog"
      :visible="showReceptionDialog"
      @close="showReceptionDialog = false"
      @continue="onContinueFromReceptionSelector"
      :multiselect="true"
      :filterData="{
        transaction_type: 'RECEPTION',
        has_invoice: false,
        department_uuid: localData.department_uuid,
        no_commit: true,
        partner_uuid: localData.partner_uuid,
      }"
    />
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { getQuantityFormatter } from "~/helpers/stockHelper";
import DxDataGrid, {
  DxColumn,
  DxEditing,
  DxHeaderFilter,
  DxFilterRow,
  DxSelection,
  DxTexts,
  DxRequiredRule,
  DxCustomRule,
} from "devextreme-vue/data-grid";
import ReceptionSelector from "@/components/ReceptionSelector.vue";
import GridPanelHeader from "@/components/DxGrid/GridPanelHeader.vue";

export default {
  name: "InvoiceReceptionItems",
  props: {
    formData: {
      type: Object,
      required: true,
    },
  },
  data() {
    return {
      gridAttributes: {
        class: "smagria-grid reception-items-for-invoice-grid",
      },
      localData: { ...this.formData },
      showReceptionDialog: false,
      editedDataField: null,
    };
  },
  computed: {
    ...mapGetters({
      getDefaultDateFormat: "settings/getDefaultDateFormat",
      smProductVatList: "smProducts/smProductVatList",
    }),
    quantityFormatter() {
      return getQuantityFormatter("Kg");
    },
  },
  components: {
    DxDataGrid,
    DxColumn,
    DxEditing,
    DxHeaderFilter,
    DxFilterRow,
    DxSelection,
    DxTexts,
    DxRequiredRule,
    DxCustomRule,
    ReceptionSelector,
    GridPanelHeader,
  },
  methods: {
    ...mapActions({}),
    validate() {
      this.$refs.receptionItemsGrid.instance.saveEditData();

      if (
        !this.localData.items.length ||
        this.localData.items.some((p) => !p.quantity || p.quantity === 0) ||
        this.localData.items.some((p) => !p.unit_price_discounted || p.unit_price_discounted === 0)
      ) {
        return [this.$t("validation.reception_and_quantity_and_price_required")];
      }

      return [];
    },
    updateData(newData) {
      if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
        this.localData = { ...newData }; // Update localData with new data
      }
    },
    onEditingStart(e) {
      //e.cancel = true; // Cancel editing for this row
      this.editedDataField = e.column.dataField || null;
    },
    handleAddClick(e) {
      this.showReceptionDialog = true; // Show the reception selector dialog
    },
    onRowUpdated(e) {
      const row = e.data;
      // Find the item in localData.items by uuid
      const item = this.localData.items.find((i) => i.uuid === row.uuid);
      if (!item) return;

      // Use the updated values or fallback to existing
      const unit_price = row.unit_price !== undefined ? row.unit_price : item.unit_price;
      const discount = row.discount !== undefined ? row.discount : item.discount;
      const vat_code = row.vat_code !== undefined ? row.vat_code : item.vat_code;
      const quantity = row.quantity !== undefined ? row.quantity : item.quantity;
      let price_netto = row.price_netto !== undefined ? parseFloat(row.price_netto) : parseFloat(item.price_netto);

      // If price_netto was changed, recalculate unit_price and unit_price_discounted
      if (this.editedDataField === "price_netto" && row.price_netto !== undefined) {
        // Calculate unit_price_discounted from price_netto and quantity
        item.unit_price_discounted = quantity > 0 ? (price_netto / quantity).toFixed(3) : 0;
        // Calculate unit_price from discount and unit_price_discounted
        item.unit_price =
          discount > 0
            ? ((parseFloat(item.unit_price_discounted) * 100) / (100 - discount)).toFixed(3)
            : parseFloat(item.unit_price_discounted);
      } else {
        // Normal calculation if price_netto was not changed
        item.unit_price_discounted = discount > 0 ? (unit_price * (1 - discount / 100)).toFixed(3) : unit_price;
      }

      // Always recalculate price_netto and price_brutto
      item.price_netto = (item.unit_price_discounted * quantity).toFixed(2);
      if(vat_code !== undefined && vat_code !== null && vat_code > 0){
        item.price_brutto = (item.unit_price_discounted * quantity * (1 + vat_code / 100)).toFixed(2);
      } else {
        item.price_brutto = (item.unit_price_discounted * quantity).toFixed(2);
      }
    },
    onContinueFromReceptionSelector(selectedReception) {
      selectedReception.forEach((item) => {
        if (!this.localData.items.some((p) => p.uuid === item.uuid)) {
          const product = item.product;

          const unit_price_discounted =
            (this.localData.discount ?? 0) > 0 ? item.unit_price * (1 - this.localData.discount / 100) : item.unit_price;

          const vat_code = this.localData.vat_code ?? item.vat_code;
          const price_brutto =
            vat_code !== undefined && vat_code !== null && vat_code > 0
              ? unit_price_discounted * item.quantity * (1 + vat_code / 100)
              : unit_price_discounted * item.quantity;

          this.localData.items.push({
            ...{
              uuid: item.uuid,
              invoice_item_uuid: null,
              product_uuid: product.uuid,
              product_name: product.name,
              quantity: item.quantity,
              unit_price: item.unit_price,
              unit_price_discounted: unit_price_discounted,
              vat_code: vat_code,
              product_code: product.code,
              document_number: item.document?.document_header?.current_model ?? "-",
              measure_unit: product.measure_unit_name,
              measure_unit_id: product.measure_unit_id,
              price_netto: (unit_price_discounted * item.quantity).toFixed(2),
              price_brutto: price_brutto.toFixed(2),
              discount: this.localData.discount ?? 0,
              original_unit_price: item.unit_price,
              transaction_uuid: item.uuid,
            },
          });
        }
      });

      this.showReceptionDialog = false; // Close the dialog after selection
    },
    async onRowRemoving(e) {
      /*
      const productName = e.data.name;
      const confirmed = await window.confirm(
        this.$t("common.confirm_delete", { title: productName }),
        this.$t("common.confirm_delete_title")
      );
      if (!confirmed) {
        e.cancel = true;
      }*/
    },
    onCellPrepared(e) {
      // Only for data rows and editable columns
      if (e.rowType === "data" && e.column.allowEditing) {
        e.cellElement.classList.add("editable-cell-yellow");
      }
    },
  },
  watch: {
    formData: {
      deep: true,
      handler(newData) {
        if (JSON.stringify(this.localData) !== JSON.stringify(newData)) {
          this.localData = { ...newData };
        }
      },
    },
    localData: {
      deep: true,
      handler(newData) {
        this.$emit("input", newData);
        this.$emit("update-form-data", newData, 2);
      },
    },
  },
};
</script>

<style lang="scss" scoped>
.reception-items-for-invoice-grid {
  height: 500px;
}
</style>
